import type { SubjectOf } from './subjects'
import { type Failure, RequestFailed, malformed, readJson, request } from './request'

/**
 * The tasks, as the service describes it.
 *
 * The whole list arrives in one request, **already in the order it is drawn**. The order is not this
 * side's decision and is deliberately not re-sorted here: it comes out of one index in the database,
 * and a second sorting in TypeScript would be a second opinion about what "soonest" means.
 *
 * What *is* this side's decision is what counts as **overdue**, and that is on purpose. A day is a day
 * where somebody is standing: a task due on the 9th is late in Auckland hours before it is late in
 * Berlin. So the service sends `dueOn` as a plain `YYYY-MM-DD` and {@link daysLeft} works it out here.
 */

export interface Task {
  readonly id: string
  /** What is to be done. One sentence, never empty. */
  readonly text: string
  /** The day it is due as `YYYY-MM-DD`, or `null` for one that is simply open. */
  readonly dueOn: string | null
  /** When it was ticked off, or `null` while it is open. */
  readonly doneAt: string | null
  readonly createdAt: string
  readonly updatedAt: string
  /** The subjects it belongs to. Always filed by hand — there is no folder to inherit from. */
  readonly subjects?: readonly SubjectOf[]
}

/**
 * Whether it has been ticked off.
 *
 * One function rather than a field on the wire, because the moment is the thing that is stored: the
 * retention counts from it, so a boolean beside it would be a second copy of the same fact.
 */
export function isDone(task: Task): boolean {
  return task.doneAt !== null
}

const TASKS = '/api/tasks'

export async function fetchTasks(signal?: AbortSignal): Promise<Task[]> {
  const response = await request(TASKS, { signal })
  const payload: unknown = await response.json().catch(() => null)
  if (!Array.isArray(payload)) {
    throw malformed('The answer did not describe a list of tasks.')
  }
  return payload as Task[]
}

/** A new open point: one sentence, and a day only if it has one. */
export async function addTask(text: string, dueOn: string | null = null): Promise<Task> {
  return (await change(TASKS, 'POST', { text, dueOn })) as Task
}

/** What it says, changed where it stands. */
export async function changeTaskText(id: string, text: string): Promise<Task> {
  return (await change(`${TASKS}/${encodeURIComponent(id)}/text`, 'PUT', { text })) as Task
}

/** Gives it a day, or takes the day away again with `null`. */
export async function changeTaskDue(id: string, dueOn: string | null): Promise<Task> {
  return (await change(`${TASKS}/${encodeURIComponent(id)}/due`, 'PUT', { dueOn })) as Task
}

/**
 * Ticks it off, or takes that back.
 *
 * The retention starts at this moment rather than at the first time it was ticked off, so a task
 * handled again stands for the whole period again.
 */
export async function tickOffTask(id: string, done: boolean): Promise<Task> {
  return (await change(`${TASKS}/${encodeURIComponent(id)}/done`, done ? 'POST' : 'DELETE')) as Task
}

/**
 * Removes a task, now and for good.
 *
 * Nothing is asked. A task is a sentence and is typed again in five seconds, and it already has a
 * state that can be taken back — being ticked off. Two ways back for one small thing would be two
 * nobody can tell apart.
 */
export async function removeTask(id: string): Promise<void> {
  await change(`${TASKS}/${encodeURIComponent(id)}`, 'DELETE')
}

/**
 * How many days are left, counted in whole days from today where the reader is standing.
 *
 * Negative for something overdue, `0` for today, `null` for a task with no day. Both ends are counted
 * from midnight, so "tomorrow" is one whatever the time is now — a task due tomorrow must not become
 * "in 0 days" at half past eleven tonight.
 */
export function daysLeft(task: Task, today: Date = new Date()): number | null {
  if (task.dueOn === null) {
    return null
  }
  const parts = task.dueOn.split('-').map(Number)
  const [year, month, day] = parts
  if (parts.length !== 3 || parts.some(Number.isNaN)) {
    return null
  }
  const due = new Date(year as number, (month as number) - 1, day as number)
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((due.getTime() - from.getTime()) / MILLISECONDS_A_DAY)
}

const MILLISECONDS_A_DAY = 24 * 60 * 60 * 1000

/**
 * What is left, in the words somebody would use.
 *
 * Days near today, a date further out: "in 47 days" says less than "12 Oct", and "yesterday" says more
 * than either. Empty for a task with no day — there is nothing to say about it, which is exactly what
 * standing at the top of the list already says.
 */
export function timeLeft(task: Task, today: Date = new Date()): string {
  const left = daysLeft(task, today)
  if (left === null) {
    return ''
  }
  if (left === 0) {
    return 'Today'
  }
  if (left === 1) {
    return 'Tomorrow'
  }
  if (left === -1) {
    return 'Yesterday'
  }
  if (left < 0) {
    return `${String(-left)} days ago`
  }
  if (left <= DAYS_WORTH_COUNTING) {
    return `In ${String(left)} days`
  }
  return asDay(task.dueOn ?? '')
}

/** Beyond a fortnight a count of days says less than a date does. */
const DAYS_WORTH_COUNTING = 14

/** A day as somebody reads it: `12 Oct 2026`, in whatever the browser's own conventions are. */
export function asDay(dueOn: string): string {
  const at = new Date(`${dueOn}T00:00:00`)
  if (Number.isNaN(at.getTime())) {
    return dueOn
  }
  return at.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** Today as `YYYY-MM-DD`, which is what a date field wants and what the service reads. */
export function todayAsDay(today: Date = new Date()): string {
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${String(today.getFullYear())}-${month}-${day}`
}

/** The statuses that carry a code this side knows how to say out loud. */
const REFUSALS = [400, 404, 409, 413] as const

const SENTENCES: Readonly<Record<string, string>> = {
  'no-such-task': 'That task is not there any more.',
  'empty-task': 'A task has to say something.',
  'task-too-long':
    'That is longer than a task holds. Something this long wants to be a note or a document.',
}

async function change(address: string, method: string, body?: unknown): Promise<unknown> {
  const response = await request(address, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    expect: [...REFUSALS],
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (!response.ok) {
    throw refusalIn(await response.text(), response.status)
  }

  return response.status === 204 ? null : readJson<unknown>(response)
}

function parse(body: string): { code?: string; message?: string } | null {
  try {
    return JSON.parse(body) as { code?: string; message?: string }
  } catch {
    return null
  }
}

function refusalIn(body: string, status: number): RequestFailed {
  const problem = parse(body)
  const known = problem?.code === undefined ? undefined : SENTENCES[problem.code]

  const failure: Failure = {
    kind: status >= 500 ? 'service' : 'refused',
    message: known ?? problem?.message ?? 'MyLib could not do that.',
    detail: known === undefined ? null : (problem?.message ?? null),
    // Nothing on this list gets better by being asked again: each of them is an answer.
    retryable: status >= 500,
    code: problem?.code ?? null,
  }
  return new RequestFailed(failure)
}
